import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react'
import { PermissionTypes, UserPermissionsKeyTypes } from 'models/Users/UserModel'
import type { ProfileUpdateRequest } from 'models/ota/ProfileUpdateRequestModel'
import type {
    WithCommonSettingsStoreStore,
    WithProfilesStore,
    WithProfileUpdateRequestsStore,
    WithVehiclesStore,
} from 'stores/RootStoreModel'
import { isOversightControl } from 'stores/rootStore'
import { showNotificationForStatus, showNotificationsForStatusMap } from 'utils/NotificationUtils'
import { sortUpdateRequests } from './sortUpdateRequests'
import { UpdateRequestRowContainer } from './UpdateRequestRowContainer'
import { UpdateRequestsViewer } from './UpdateRequestsViewer'

const POLLING_INTERVAL_MS = 10_000

const getSubTitle = (requests: ReadonlyArray<ProfileUpdateRequest>) => {
    const vehicleIds = new Set(requests.map(request => request.vehicle_id))
    const [singleVehicleId] = vehicleIds
    return vehicleIds.size === 1 && singleVehicleId !== undefined ? `Vehicle ${singleVehicleId}` : `${vehicleIds.size} vehicles`
}

// The message of the requests when they all have the same one, e.g. when they all failed for the same reason
const getSharedMessage = (requests: ReadonlyArray<ProfileUpdateRequest>) => {
    const messages = new Set(requests.map(request => request.message))
    const [sharedMessage] = messages
    return messages.size === 1 && sharedMessage ? sharedMessage : undefined
}

interface UpdateRequestsViewerContainerProps extends WithProfileUpdateRequestsStore, WithProfilesStore, WithCommonSettingsStoreStore, WithVehiclesStore { }

// Keeps the live or pending profile update requests up to date, and shows them on all the pages while there are any
export const UpdateRequestsViewerContainer = observer(({
    profileUpdateRequestsStore,
    profilesStore,
    commonSettingsStore,
    vehiclesStore,
}: UpdateRequestsViewerContainerProps) => {
    // Profile updates (OTA) exist only in oversight control. Shown only to the users who can request and dismiss them
    // (all the roles can view OTA).
    const canView = isOversightControl() && commonSettingsStore.hasPermission(UserPermissionsKeyTypes.Ota, PermissionTypes.Edit)
    const { requests, fetchStatus } = profileUpdateRequestsStore

    showNotificationsForStatusMap(profileUpdateRequestsStore.dismissStatuses, `Error dismissing profile update request`)
    // Also for the profiles fetched by the vehicles page: this container is on all the pages (its hooks run also while
    // it's hidden), so a failure is shown once, on any page.
    showNotificationForStatus(profilesStore.fetchStatus, `Error loading profiles`)

    // For the titles of the requests' profiles on all the pages (the profile ids are shown without them)
    useEffect(() => {
        if (canView) {
            profilesStore.fetchProfilesIfNeeded()
        }
    }, [canView, profilesStore])

    useEffect(() => {
        if (!canView) {
            return
        }
        const fetchRequests = () => {
            if (!profileUpdateRequestsStore.fetchStatus.inProgress) {
                void profileUpdateRequestsStore.getLiveOrPendingRequests()
            }
        }
        // Paused while the browser tab is hidden, and fetched at once when it is shown again
        let interval: ReturnType<typeof setInterval> | undefined
        const startPolling = () => {
            fetchRequests()
            interval = setInterval(fetchRequests, POLLING_INTERVAL_MS)
        }
        const stopPolling = () => { clearInterval(interval) }
        const onVisibilityChange = () => {
            stopPolling()
            if (!document.hidden) {
                startPolling()
            }
        }
        if (!document.hidden) {
            startPolling()
        }
        document.addEventListener('visibilitychange', onVisibilityChange)
        return () => {
            stopPolling()
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [canView, profileUpdateRequestsStore])

    // An ended request (succeeded or failed) changed the vehicle's profile, so the vehicles' profiles are refetched to show it
    const endedRequestIds = requests.filter(request => request.isEnded).map(request => request.id)
    const seenEndedRequestIds = useRef<Set<number> | null>(null)
    useEffect(() => {
        if (!fetchStatus.isSuccess && seenEndedRequestIds.current === null) {
            return
        }
        const seenIds = seenEndedRequestIds.current
        seenEndedRequestIds.current = new Set(endedRequestIds)
        // The requests that ended before the first fetch are already in the vehicles fetched by their page
        const hasNewEnded = seenIds !== null && endedRequestIds.some(id => !seenIds.has(id))
        if (hasNewEnded && vehiclesStore.vehicles !== null) {
            void vehiclesStore.refreshVehicleProfiles()
        }
    }, [endedRequestIds.join(), fetchStatus.isSuccess])

    if (!canView || requests.length === 0) {
        return null
    }

    const sharedMessage = getSharedMessage(requests)
    return (
        <UpdateRequestsViewer {...{
            subTitle: getSubTitle(requests),
            message: sharedMessage,
            dismissAll: profileUpdateRequestsStore.isAllDismissible
                ? {
                    isDismissing: requests.some(request => profileUpdateRequestsStore.isDismissing(request.id)),
                    onDismissAll: () => { void profileUpdateRequestsStore.dismissAll() },
                }
                : undefined,
        }}>
            { sortUpdateRequests(requests).map(request =>
                <UpdateRequestRowContainer {...{ key: request.id, request, profilesStore, profileUpdateRequestsStore, showMessage: sharedMessage === undefined }} />
            )}
        </UpdateRequestsViewer>
    )
})
